import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Trash2, Check } from "lucide-react";

export const ReportsPanel = () => {
  const [reports, setReports] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports", { params: { status: "open" } });
      setReports(data);
    } catch {
      setReports([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    try {
      await api.post(`/reports/${id}/resolve`, { action });
      toast.success(action === "listing_removed" ? "Listing removed and report closed" : "Report dismissed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!reports) return null;

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="reports-panel">
      <div className="flex items-center gap-2">
        <Flag className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-black text-xl tracking-tight">Listing reports</h2>
        <span className="ml-2 text-xs text-neutral-500">{reports.length} open · DSA notice-and-action</span>
      </div>
      {reports.length === 0 ? (
        <p className="mt-4 text-sm text-neutral-500" data-testid="reports-empty">No open reports — all clear.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid="report-row" className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] font-bold">
                  {r.reason}
                </span>
                <span className="font-semibold text-white">{r.product_title || r.product_id}</span>
                <span className="text-xs text-neutral-500 ml-auto">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                </span>
              </div>
              {r.details && <p className="mt-2 text-xs text-neutral-400 leading-relaxed">{r.details}</p>}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="report-dismiss-btn"
                  onClick={() => resolve(r.id, "dismiss")}
                  className="rounded-full h-8 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white text-xs"
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> Dismiss
                </Button>
                <Button
                  size="sm"
                  data-testid="report-remove-listing-btn"
                  onClick={() => resolve(r.id, "listing_removed")}
                  className="rounded-full h-8 bg-[#FF3B30] hover:bg-[#e0342a] text-white text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove listing
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
